import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Link from '@/components/Link'

import DefaultComponentProps from '@/types/DefaultComponentProps'

import i18nextConfig from '../../../next-i18next.config'

import styles from './LangSwitcher.module.scss'
import { routes } from '@/config'

const LangSwitcher: FC<DefaultComponentProps> = ({ className = '' }) => {
  const {
    i18n: { language }
  } = useTranslation()
  const router = useRouter()

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    window.location.href = e.currentTarget.href
  }

  const url = useMemo(() => router?.asPath ?? routes.HOME, [router])

  const [bgVars, setBgVars] = useState<Record<string, string>>({})
  const optionsRef = useRef<Record<string, HTMLLIElement | null>>({})

  const calcVars = useCallback((target: HTMLElement | null | undefined) => {
    const { width = 0 } = target?.getBoundingClientRect() ?? {}
    const left = target?.offsetLeft ?? 0

    setBgVars({
      '--bg-opacity': '1',
      '--bg-left': `${left}px`,
      '--bg-width': `${width}px`
    })
  }, [])

  const handleMouseLeave = useCallback(() => {
    calcVars(optionsRef.current[language])
  }, [language, calcVars])

  const handleMouseEnter = useCallback(
    (target: HTMLElement | null | undefined) => {
      calcVars(target)
    },
    [calcVars]
  )

  useEffect(() => {
    if (!optionsRef.current[language]) {
      return
    }
    calcVars(optionsRef.current[language])
  }, [language, calcVars])

  return (
    <nav
      className={clsx(styles['lang-switcher'], className)}
      style={bgVars as React.CSSProperties}
    >
      <ul className={styles['lang-switcher__list']}>
        {i18nextConfig.i18n.locales.map(locale => (
          <li
            key={locale}
            className={styles['lang-switcher__item']}
            onMouseEnter={() => handleMouseEnter(optionsRef.current[locale])}
            onMouseLeave={handleMouseLeave}
            ref={el => (optionsRef.current[locale] = el)}
          >
            <Link
              onClick={handleClick}
              className={clsx(styles['lang-switcher__link'], {
                [styles['lang-switcher__link_active']]: language === locale
              })}
              href={url}
              locale={locale}
              title={locale}
            >
              {locale.toUpperCase()}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  )
}

export default LangSwitcher
